// Decoding establishes correlation/freshness only. The gateway, not the
// browser, authenticates a proposed token before it can replace credentials.
export function readWidgetTokenClaims(token: string): Record<string, any> | undefined {
  try {
    if (typeof token !== "string" || token.length > 8192) return;
    const parts = token.split(".");
    if (parts.length !== 3 || parts.some(part => !part)) return;
    const encoded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(encoded), char => char.charCodeAt(0))));
  } catch { return; }
}

function identity(claims: Record<string, any>) {
  const audience = Array.isArray(claims?.aud) ? claims.aud : [claims?.aud];
  if (![claims?.tenantId, claims?.ext, claims?.sub, claims?.iss, ...audience]
    .every(value => typeof value === "string" && value.length > 0 && value.length <= 512) || !audience.length) return;
  return JSON.stringify([claims.tenantId, claims.ext, claims.sub, claims.iss, [...audience].sort()]);
}

export function widgetCredentialVersion(token: string) {
  const claims = readWidgetTokenClaims(token);
  if (!claims || !identity(claims) || !Number.isInteger(claims.iat) || !Number.isInteger(claims.exp)) return;
  return { issuedAt: claims.iat, expiresAt: claims.exp };
}

export async function widgetCredentialBinding(token: string, gatewayUrl: string): Promise<string | undefined> {
  const claims = readWidgetTokenClaims(token);
  const scope = claims && identity(claims);
  if (!scope) return;
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify([gatewayUrl, scope])));
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, "0")).join("");
}

export function newerCredentialVersion(candidate, current): boolean {
  return Boolean(candidate && current && Number.isInteger(candidate.issuedAt) && Number.isInteger(candidate.expiresAt)
    && Number.isInteger(current.issuedAt) && Number.isInteger(current.expiresAt)
    && (candidate.issuedAt > current.issuedAt ||
      candidate.issuedAt === current.issuedAt && candidate.expiresAt > current.expiresAt));
}

export function newerWidgetCredential(candidate: string, current: string, now = Date.now() / 1000) {
  const proposed = readWidgetTokenClaims(candidate), previous = readWidgetTokenClaims(current);
  if (!proposed || !previous || !identity(proposed) || identity(proposed) !== identity(previous)) return;
  if (!newerCredentialVersion(widgetCredentialVersion(candidate), widgetCredentialVersion(current)) ||
    proposed.iat <= 0 || proposed.iat > now + 60 || proposed.exp <= now ||
    proposed.exp <= proposed.iat || proposed.exp - proposed.iat > 3600 ||
    (proposed.nbf !== undefined && (!Number.isFinite(proposed.nbf) || proposed.nbf > now + 30))) return;
  return proposed;
}

type Context = { token: string; key: string; url: string };
type Phase = "applied" | "rejected" | "timeout" | "failed" | "superseded";

// One bounded verification at a time. Failed offers can be retried by the
// existing owner heartbeat after 30s; this creates no token refresh loop.
export class WidgetCredentialSync {
  private running?: Promise<void>;
  private controller?: AbortController;
  private generation = 0;
  private lastAttempt?: { token: string; key: string; at: number };
  constructor(private options: {
    context(): Context | undefined;
    verify(url: string, token: string, signal: AbortSignal): Promise<Record<string, any>>;
    apply(token: string, config: Record<string, any>, expiresAt: number): void;
    observe?(phase: Phase): void;
    now?(): number;
    timeoutMs?: number;
  }) {}
  private now() { return this.options.now?.() ?? Date.now(); }
  private observe(phase: Phase) { try { this.options.observe?.(phase); } catch { /* Observation is independent. */ } }
  accept(token: string): Promise<void> {
    const context = this.options.context();
    const proposed = context && newerWidgetCredential(token, context.token, this.now() / 1000);
    if (!context || !proposed || this.running) return Promise.resolve();
    if (this.lastAttempt?.token === token && this.lastAttempt.key === context.key && this.now() - this.lastAttempt.at < 30000) return Promise.resolve();
    this.lastAttempt = { token, key: context.key, at: this.now() };
    const generation = this.generation;
    const controller = new AbortController();
    this.controller = controller;
    this.running = (async () => {
      let timer: ReturnType<typeof setTimeout>, timedOut = false;
      const canceled = new Promise<never>((_, reject) => {
        controller.signal.addEventListener("abort", () => reject(new DOMException("Credential verification canceled", "AbortError")), { once: true });
        timer = setTimeout(() => { timedOut = true; controller.abort(); }, this.options.timeoutMs ?? 8000);
      });
      try {
        const config = await Promise.race([canceled, Promise.resolve().then(() => this.options.verify(context.url, token, controller.signal))]);
        const current = this.options.context();
        if (generation !== this.generation || !current || current.key !== context.key || current.url !== context.url ||
          !newerWidgetCredential(token, current.token, this.now() / 1000)) { this.observe("superseded"); return; }
        if (config.tenantId !== proposed.tenantId || config.userId !== proposed.sub || config.extension !== proposed.ext ||
          !Array.isArray(config.iceServers) || !Number.isFinite(config.iceServersExpiresAtUnix) || config.iceServersExpiresAtUnix < 0) {
          this.observe("rejected"); return;
        }
        this.options.apply(token, config, proposed.exp);
        this.observe("applied");
      } catch {
        this.observe(generation !== this.generation ? "superseded" : timedOut ? "timeout" : "failed");
      } finally { clearTimeout(timer!); this.controller = undefined; this.running = undefined; }
    })();
    return this.running;
  }
  cancel() { this.generation++; this.controller?.abort(); this.lastAttempt = undefined; }
}
